/**
 * Group a workflow's inputs into PARTY BLOCKS — the inputs that share one
 * `constraints.entitySource.role` (MAR-176), which describe one party of the instrument (the
 * trabajador's name, DUI, nacionalidad, domicilio…).
 *
 * The ONE grouping (MAR-948): the chat card (`WorkflowCard.vue`), the context-rail visualization
 * (`ContextRailWorkflowDraft.vue`, MAR-437 follow-up) and the run form's per-section fill bars
 * (`WorkflowRunForm.vue`) all read it, so a role groups the same fields on every surface. Returns
 * EVERY block, filled or not — a caller that only wants the incomplete ones (the chat card) filters
 * on `anyBlank` itself; the rail shows all of them, since it's a standing view of who is assigned,
 * not just a checklist of what's missing. Guard: `tests/unit/workflowFieldHelpersGuard.test.ts`.
 */
import type { EntityCandidate } from '#shared/entities/entityCandidate'
import { findIdentity, indexIdentities } from '#shared/entities/identityNumber'
import { formatIdentity, idTypeForProperty, isIdentifierProperty, offerableIdTypes, type EntityIdType } from '#shared/entities/vocabulary'
import { foldValue, valueForTarget } from '#shared/entities/partyValues'
import { isBlankValue } from './workflowFields'

export interface PartyBlockTarget {
  inputKey: string
  label: string
  field: string
  options?: unknown[]
}

export interface PartyBlock {
  role: string
  kind: string | null
  idType: string | null
  targets: PartyBlockTarget[]
  anyBlank: boolean
}

export interface ResolvedBlockTarget {
  target: PartyBlockTarget
  value: string
}

export function isNameTargetField(field: string): boolean {
  return field === 'name' || field.endsWith('.name')
}

/**
 * A readable title for a block, from the template's own free-text `role` (MAR-437 follow-up).
 *
 * `role` is free text per template (`shared/templates/schema.ts`: "an acta has a presidente, a
 * finiquito a trabajador"), so this can only humanize it generically — capitalize, underscores to
 * spaces — not map it through a fixed vocabulary. Shared between `EntityPartyFill.vue` (the block's
 * own header) and `ContextRailWorkflowDraft.vue`'s read-only completed summary, so a role reads the
 * same in both places.
 */
export function humanizeRole(role: string): string {
  const words = role.replace(/[_-]+/g, ' ').trim().split(/\s+/)
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

export interface PartyBlockOptions {
  /**
   * A role to leave out entirely. The run form passes the BATCHED role: in batch mode that party is
   * picked per row inside the table, and a fill bar above the section would be a second writer for
   * the values the table owns.
   */
  excludeRole?: string | null
}

export function computePartyBlocks(
  fields: readonly { key: string, label: string, constraints?: any, options?: unknown[] }[],
  values: Record<string, unknown>,
  opts: PartyBlockOptions = {},
): PartyBlock[] {
  const byRole = new Map<string, PartyBlock>()
  for (const field of fields) {
    const src = field.constraints?.entitySource
    // Both halves are required: a role with no `field` says which party the input belongs to but
    // not what to write into it. A field with no role is a single-input suggestion, unrelated to a
    // block — `WorkflowFieldControl` renders it under the input itself (MAR-129).
    if (!src?.role || !src?.field) continue
    if (opts.excludeRole && src.role === opts.excludeRole) continue
    const block: PartyBlock = byRole.get(src.role) ?? {
      role: src.role,
      kind: null,
      idType: null,
      targets: [],
      anyBlank: false,
    }
    // `options` travels with the target so a `select` is filled with one of ITS option values
    // rather than the source document's wording — see `valueForTarget`.
    block.targets.push({
      inputKey: field.key,
      label: field.label,
      field: src.field,
      options: field.options ?? field.constraints?.options,
    })
    if (isBlankValue(values[field.key])) block.anyBlank = true
    const inferredIdType = src.idType ?? idTypeForProperty(src.field)
    if (inferredIdType) block.idType = inferredIdType
    const partyKind = field.constraints?.partyKind ?? src.kind
    if (typeof partyKind === 'string' && partyKind) block.kind = partyKind
    byRole.set(src.role, block)
  }
  return [...byRole.values()]
}

/**
 * Filter entity candidates by a party block's slot kind (`organization`, `person`, or `null` for
 * unfiltered).
 */
export function filterCandidatesByKind<T extends { kind?: string | null }>(
  candidates: readonly T[],
  kind?: string | null,
): T[] {
  if (!kind) return [...candidates]
  return candidates.filter(c => c.kind === kind)
}

/**
 * Partition a party block's targets into the facts the picked candidate resolves and the targets it
 * cannot fill (AC5, AC6).
 */
export function resolveBlockTargets(
  block: PartyBlock,
  candidate: EntityCandidate,
): { resolved: ResolvedBlockTarget[], missing: PartyBlockTarget[] } {
  const resolved: ResolvedBlockTarget[] = []
  const missing: PartyBlockTarget[] = []
  for (const target of block.targets) {
    const value = valueForTarget(candidate, target)
    if (!isBlankValue(value)) {
      resolved.push({ target, value })
    } else {
      missing.push(target)
    }
  }
  return { resolved, missing }
}

/**
 * Clear every auto-filled target of a party block except its `name` field when switching to manual
 * entry ("Escribir a mano", AC7).
 */
export function clearPickedBlockValues(
  block: PartyBlock,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...values }
  for (const target of block.targets) {
    if (isNameTargetField(target.field)) continue
    next[target.inputKey] = ''
  }
  return next
}

/**
 * Match a party block's prefilled values (from "Edit & Re-run") against loaded candidates (MAR-1038 AC9).
 *
 * Matches by canonical identifier first (`findIdentity`, which strips hyphens and leading zeros).
 * If a non-blank identifier is present in `values` and matches no candidate, returns `null` rather
 * than falling back to a different entity that shares the same name. When no identifier is prefilled,
 * matches by folded `name` (or composed `given_names` + `surnames`).
 */
export function matchPrefilledPartyCandidate(
  block: PartyBlock,
  values: Record<string, unknown>,
  candidates: readonly EntityCandidate[],
): EntityCandidate | null {
  const pool = filterCandidatesByKind(candidates, block.kind)
  if (pool.length === 0) return null

  const idTargets = block.targets.filter(t => isIdentifierProperty(t.field))
  let hadNonBlankId = false
  for (const tgt of idTargets) {
    const rawId = String(values[tgt.inputKey] ?? '').trim()
    if (!rawId) continue
    hadNonBlankId = true
    const wantedType = (idTypeForProperty(tgt.field) ?? block.idType ?? null) as EntityIdType | null
    const compatibleTypes = wantedType ? new Set(offerableIdTypes(wantedType)) : null
    const idPool: { candidate: EntityCandidate, number: string }[] = []
    for (const c of pool) {
      if (c.idNumber && (!compatibleTypes || !c.idType || compatibleTypes.has(c.idType))) {
        idPool.push({ candidate: c, number: c.idNumber })
      }
      if (wantedType === 'tax_id' && c.attributes?.tax_id) {
        idPool.push({ candidate: c, number: c.attributes.tax_id })
      }
    }
    const hit = findIdentity(indexIdentities(idPool, entry => entry.number), rawId)
    if (hit) return hit.candidate
  }
  if (hadNonBlankId) return null

  const nameTarget = block.targets.find(t => isNameTargetField(t.field))
  let rawName = nameTarget ? String(values[nameTarget.inputKey] ?? '').trim() : ''
  if (!rawName) {
    const givenTarget = block.targets.find(t => t.field === 'given_names' || t.field.endsWith('.given_names'))
    const surTarget = block.targets.find(t => t.field === 'surnames' || t.field.endsWith('.surnames'))
    const given = givenTarget ? String(values[givenTarget.inputKey] ?? '').trim() : ''
    const sur = surTarget ? String(values[surTarget.inputKey] ?? '').trim() : ''
    rawName = [given, sur].filter(Boolean).join(' ')
  }
  const folded = foldValue(rawName)
  if (!folded) return null
  return pool.find(c => c.name === rawName || foldValue(c.name) === folded) ?? null
}

export type PartyProvenanceTranslator = (
  key: string,
  params?: Record<string, unknown>,
  plural?: number,
) => string

/**
 * Format an entity candidate's provenance label for both the inline party picker and the
 * picked-party summary card (MAR-1221).
 */
export function formatPartyProvenance(
  candidate: EntityCandidate,
  t: PartyProvenanceTranslator,
): string {
  if (candidate.provenance.via === 'case') {
    return t('workflowRun.suggestions.fromDocument', { name: candidate.provenance.documentName })
  }
  const rel = candidate.provenance.relationship
  if (rel.startsWith('mentions:')) {
    const n = Number.parseInt(rel.slice('mentions:'.length), 10) || 1
    return t('workflowRun.suggestions.fromMentions', { n }, n)
  }
  if (rel === 'organization' || rel === 'identity') {
    return t('workflowRun.suggestions.fromOrganization')
  }
  return t('workflowRun.suggestions.fromClient')
}

/**
 * Compose the secondary identity/provenance metadata line (`NIT 0614-... · San Salvador · De este cliente`)
 * shared between `EntityPartyPicker.vue` (`includeCity: true`) and `PickedPartyCard.vue`.
 */
export function formatPartyMetaLine(
  candidate: EntityCandidate,
  t: PartyProvenanceTranslator,
  locale?: string | null,
  opts: { includeCity?: boolean } = {},
): string {
  const lang = locale === 'en' ? 'en' : 'es'
  const idText = formatIdentity(candidate.idType, candidate.jurisdiction, candidate.idNumber, lang) ?? ''
  const city = opts.includeCity
    ? (candidate.attributes?.municipality || candidate.attributes?.department || '')
    : ''
  const prov = formatPartyProvenance(candidate, t)
  return [idText, city, prov].filter(Boolean).join(' · ')
}
